import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { Upload as UploadIcon, CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { searchCompanies, getCompany } from '../../api/companies';
import CompanyForm from '../../components/entities/CompanyForm/CompanyForm';
import { CompanyCreate, CompanyUpdate, CompanyListItem, Company } from '../../types/company';
import { createCompany, addTechnologyToCompany } from '../../api/companies';
import { getTechnology } from '../../api/technologies';

interface ImportCompany extends CompanyCreate {
  _importStatus: 'to_review' | 'submitted' | 'cancelled';
  _importId: string;
  _similarCompanies?: CompanyListItem[];
  _searchComplete?: boolean;
  _technologies?: Array<{ id: string }>;
  _validatedTechnologies?: Array<{ id: string; name: string }>;
  _technologyValidationComplete?: boolean;
  _invalidTechnologies?: string[];
  _savedCompany?: Company;
}

const BulkCompanyImport: React.FC = () => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';

  const [jsonInput, setJsonInput] = useState('');
  const [companies, setCompanies] = useState<ImportCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchingSimilar, setSearchingSimilar] = useState(false);
  const [validatingTechnologies, setValidatingTechnologies] = useState(false);

  // Generate ID from company name
  const generateIdFromName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file (.json)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        parseAndLoadCompanies(content);
      } catch (err: any) {
        setError('Failed to read file. Please ensure it is a valid JSON file.');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file.');
    };
    reader.readAsText(file);
  };

  // Parse JSON and load companies (shared by file upload and text input)
  const parseAndLoadCompanies = (jsonContent: string) => {
    setError(null);
    try {
      const parsed = JSON.parse(jsonContent);
      const companiesArray = Array.isArray(parsed) ? parsed : [parsed];

      // Validate that we have at least one company
      if (companiesArray.length === 0) {
        setError('No companies found in JSON. Please provide at least one company.');
        return;
      }

      // Transform to ImportCompany format
      const importCompanies: ImportCompany[] = companiesArray.map((company, index) => ({
        name: company.name || '',
        website: company.website || '',
        product_website: company.product_website || '',
        ticker: company.ticker || '',
        exchange: company.exchange || '',
        corporate_status: company.corporate_status || '',
        summary_description: company.summary_description || '',
        description: company.description || '',
        quartr_company_id: company.quartr_company_id || '',
        company_type: company.company_type || [],
        feed_sec: company.feed_sec || '',
        feed_news_rss: company.feed_news_rss || '',
        feed_events: company.feed_events || '',
        feed_press_release: company.feed_press_release || '',
        email_press_feed: company.email_press_feed || '',
        press_release_url: company.press_release_url || '',
        website_content_archive_url: company.website_content_archive_url || '',
        attribution_statement: company.attribution_statement || '',
        update_technologies: company.update_technologies || false,
        completion_score: company.completion_score || 0,
        _importStatus: 'to_review',
        _importId: `import_${Date.now()}_${index}`,
        _similarCompanies: [],
        _technologies: company.technologies || [],
      }));

      setCompanies(importCompanies);
    } catch (err: any) {
      // Provide detailed error message
      let errorMessage = 'Invalid JSON format. ';
      if (err instanceof SyntaxError) {
        errorMessage += `Syntax Error: ${err.message}`;
        // Try to extract line/column info if available
        const match = err.message.match(/position (\d+)/);
        if (match) {
          const position = parseInt(match[1]);
          const lines = jsonContent.substring(0, position).split('\n');
          const line = lines.length;
          const column = lines[lines.length - 1].length;
          errorMessage += ` (near line ${line}, column ${column})`;
        }
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }
      console.error('JSON Parse Error:', err);
      console.error('JSON Content:', jsonContent.substring(0, 500)); // Log first 500 chars
      setError(errorMessage);
    }
  };

  // Handle manual text input
  const handleLoadCompanies = () => {
    parseAndLoadCompanies(jsonInput);
  };

  // Search for similar companies when a company is selected
  const handleSelectCompany = async (importId: string) => {
    setSelectedCompanyId(importId);
    setError(null);

    const company = companies.find((c: ImportCompany) => c._importId === importId);
    if (!company || !company.name) return;

    // Only search if not already searched
    if (!company._searchComplete) {
      setSearchingSimilar(true);
      try {
        const results = await searchCompanies(company.name, 5);
        const similar = results.results; // Define similar here

        // Update the company with similar companies
        setCompanies((prev: ImportCompany[]) =>
          prev.map((c: ImportCompany) =>
            c._importId === importId ? { ...c, _similarCompanies: similar, _searchComplete: true } : c
          )
        );
      } catch (err: any) {
        console.error('Error searching for similar companies:', err);
        // Mark as searched even if error
        setCompanies((prev: ImportCompany[]) =>
          prev.map((c: ImportCompany) =>
            c._importId === importId ? { ...c, _searchComplete: true } : c
          )
        );
      } finally {
        setSearchingSimilar(false);
      }
    }

    // Validate technologies if present and not already validated
    if (company._technologies && company._technologies.length > 0 && !company._technologyValidationComplete) {
      setValidatingTechnologies(true);
      const validatedTechs: Array<{ id: string; name: string }> = [];
      const invalidTechs: string[] = [];

      try {
        for (const tech of company._technologies) {
          try {
            const technologyData = await getTechnology(tech.id);
            validatedTechs.push({ id: technologyData.id, name: technologyData.name });
          } catch (techErr: any) {
            console.error(`Technology ${tech.id} not found:`, techErr);
            invalidTechs.push(tech.id);
          }
        }

        const validated = validatedTechs; // Define validated here
        const invalid = invalidTechs; // Define invalid here

        // Update the company with validated technologies
        setCompanies((prev: ImportCompany[]) =>
          prev.map((c: ImportCompany) =>
            c._importId === importId ? {
              ...c,
              _validatedTechnologies: validated,
              _invalidTechnologies: invalid,
              _technologyValidationComplete: true
            } : c
          )
        );

        // Show error message if any technologies are invalid
        if (invalidTechs.length > 0) {
          setError(`Warning: The following technology IDs do not exist in the database: ${invalidTechs.join(', ')}. These will not be added when you save.`);
        }
      } catch (err: any) {
        console.error('Error validating technologies:', err);
        // Mark as validated even if error
        setCompanies((prev: ImportCompany[]) =>
          prev.map((c: ImportCompany) =>
            c._importId === importId ? { ...c, _technologyValidationComplete: true } : c
          )
        );
      } finally {
        setValidatingTechnologies(false);
      }
    }
  };

  // Save company and mark as submitted
  const handleSaveCompany = async (data: CompanyCreate | CompanyUpdate, id?: string) => {
    if (!selectedCompanyId) return;

    const currentCompany = companies.find((c: ImportCompany) => c._importId === selectedCompanyId);
    if (!currentCompany) return;

    setError(null);

    try {
      // Validate name exists
      if (!data.name) {
        setError('Company name is required.');
        return;
      }

      // Generate ID from name for validation
      const generatedId = generateIdFromName(data.name);

      // Check if another company in database has the same ID by searching
      const existingCompanies = await searchCompanies(generatedId, 50);
      const duplicateById = existingCompanies.results.find(c =>
        generateIdFromName(c.name) === generatedId
      );

      if (duplicateById) {
        setError(`A company with the same ID already exists: "${duplicateById.name}". Please use a different name or verify this is not a duplicate.`);
        return;
      }

      // Always creating new companies in bulk import
      const createdCompany = await createCompany(data as CompanyCreate);
      console.log('Created company:', createdCompany);

      const companyId = createdCompany.id || generatedId;

      // Add technologies if present - only add validated ones
      if (currentCompany._validatedTechnologies && currentCompany._validatedTechnologies.length > 0) {
        console.log('Technologies to add:', currentCompany._validatedTechnologies);
        console.log('Using company ID:', companyId);
        const technologyErrors: string[] = [];
        let successCount = 0;

        for (const tech of currentCompany._validatedTechnologies) {
          try {
            console.log(`Adding technology ${tech.id} to company ${companyId}`);
            await addTechnologyToCompany(companyId, tech.id);
            console.log(`Successfully added technology ${tech.id}`);
            successCount++;
          } catch (techErr: any) {
            const errorDetail = techErr.response?.data?.detail || techErr.message || 'Unknown error';
            const errorMsg = `Technology "${tech.name}" (${tech.id}): ${errorDetail}`;
            technologyErrors.push(errorMsg);
            console.error(`Failed to add technology ${tech.id}:`, techErr);
            console.error('Full error details:', {
              status: techErr.response?.status,
              statusText: techErr.response?.statusText,
              data: techErr.response?.data
            });
          }
        }

        // Show warning if some technologies failed to add
        if (technologyErrors.length > 0) {
          const totalCount = currentCompany._validatedTechnologies.length;
          const errorMessage = `Company "${data.name}" created successfully.\n\n` +
            `Technologies: ${successCount}/${totalCount} added successfully.\n\n` +
            `Failed technologies:\n${technologyErrors.map(e => `• ${e}`).join('\n')}`;
          setError(errorMessage);
        }
      }

      // Fetch the complete company data (with technologies) from the backend
      const fullCompanyData = await getCompany(companyId);
      console.log('Fetched full company data:', fullCompanyData);

      // Mark as submitted and store the full company data
      setCompanies((prev: ImportCompany[]) =>
        prev.map((c: ImportCompany) =>
          c._importId === selectedCompanyId ? { ...c, _importStatus: 'submitted', _savedCompany: fullCompanyData } : c
        )
      );

      // Move to next company
      const currentIndex = companies.findIndex((c: ImportCompany) => c._importId === selectedCompanyId);
      const nextCompany = companies.find((c: ImportCompany, idx: number) => idx > currentIndex && c._importStatus === 'to_review');

      if (nextCompany) {
        handleSelectCompany(nextCompany._importId);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save company');
    }
  };

  // Cancel company and mark as cancelled
  const handleCancelCompany = () => {
    if (!selectedCompanyId) return;

    // Mark as cancelled
    setCompanies((prev: ImportCompany[]) =>
      prev.map((c: ImportCompany) =>
        c._importId === selectedCompanyId ? { ...c, _importStatus: 'cancelled' } : c
      )
    );

    // Move to next company
    const currentIndex = companies.findIndex((c: ImportCompany) => c._importId === selectedCompanyId);
    const nextCompany = companies.find((c: ImportCompany, idx: number) => idx > currentIndex && c._importStatus === 'to_review');

    if (nextCompany) {
      handleSelectCompany(nextCompany._importId);
    }
  };

  const selectedCompany = companies.find((c: ImportCompany) => c._importId === selectedCompanyId);
  const toReviewCount = companies.filter((c: ImportCompany) => c._importStatus === 'to_review').length;
  const submittedCount = companies.filter((c: ImportCompany) => c._importStatus === 'submitted').length;
  const cancelledCount = companies.filter((c: ImportCompany) => c._importStatus === 'cancelled').length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Bulk Company Import
      </Typography>

      {companies.length === 0 ? (
        // JSON Input Section
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Import Companies from JSON
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a JSON file or paste JSON content below. Each company should include at least a "name" field.
            Other supported fields: website, ticker, exchange, corporate_status, summary_description, description, technologies, etc.
          </Typography>

          {/* File Upload Button */}
          <Box sx={{ mb: 3 }}>
            <input
              accept=".json"
              style={{ display: 'none' }}
              id="json-file-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={isViewer}
            />
            <label htmlFor="json-file-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
                disabled={isViewer}
                sx={{ mr: 2 }}
              >
                Upload JSON File
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary">
              or paste JSON below
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <TextField
            fullWidth
            multiline
            rows={15}
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder={`[\n  {\n    "name": "Example Corp",\n    "website": "https://example.com",\n    "ticker": "EXMP",\n    "summary_description": "A sample company",\n    "technologies": [{"id": "ai"}, {"id": "machine_learning"}]\n  }\n]`}
            variant="outlined"
            sx={{ mb: 2, fontFamily: 'monospace' }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={handleLoadCompanies}
            disabled={!jsonInput.trim() || isViewer}
          >
            Load from Text Input
          </Button>
        </Paper>
      ) : (
        // Split View: Sidebar + Edit Form
        <Grid container spacing={3}>
          {/* Sidebar - Company List */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, height: 'calc(100vh - 150px)', overflow: 'auto' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Companies ({companies.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<ScheduleIcon />}
                    label={`To Review: ${toReviewCount}`}
                    color="warning"
                    size="small"
                  />
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`Submitted: ${submittedCount}`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    icon={<CancelIcon />}
                    label={`Cancelled: ${cancelledCount}`}
                    color="default"
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <List disablePadding>
                {companies.map((company) => (
                  <ListItem
                    key={company._importId}
                    disablePadding
                    sx={{ mb: 0.5 }}
                  >
                    <ListItemButton
                      selected={selectedCompanyId === company._importId}
                      onClick={() => handleSelectCompany(company._importId)}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: selectedCompanyId === company._importId ? 'primary.main' : 'divider',
                      }}
                    >
                      <ListItemText
                        primary={company.name || '(Unnamed)'}
                        secondary={
                          <Chip
                            label={
                              company._importStatus === 'submitted' ? 'Submitted' :
                                company._importStatus === 'cancelled' ? 'Cancelled' :
                                  'To Review'
                            }
                            size="small"
                            color={
                              company._importStatus === 'submitted' ? 'success' :
                                company._importStatus === 'cancelled' ? 'default' :
                                  'warning'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setCompanies([]);
                  setSelectedCompanyId(null);
                  setJsonInput('');
                  setError(null);
                }}
              >
                Start Over
              </Button>
            </Paper>
          </Grid>

          {/* Main Content - Company Edit Form */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 3, height: 'calc(100vh - 150px)', overflow: 'auto' }}>
              {selectedCompany ? (
                <>
                  {/* Similar Companies Alert */}
                  {searchingSimilar ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Searching for similar companies...</Typography>
                      </Box>
                    </Alert>
                  ) : selectedCompany._searchComplete ? (
                    selectedCompany._similarCompanies && selectedCompany._similarCompanies.length > 0 ? (
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Similar companies found in database:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {selectedCompany._similarCompanies.map((similar) => (
                            <MuiLink
                              key={similar.id}
                              href={similar.website || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'block' }}
                            >
                              {similar.name} {similar.website && `(${similar.website})`}
                            </MuiLink>
                          ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Please review to avoid creating duplicates.
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography>No similar companies found in database.</Typography>
                      </Alert>
                    )
                  ) : null}

                  {/* Technology Validation Alert */}
                  {validatingTechnologies ? (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Validating technologies...</Typography>
                      </Box>
                    </Alert>
                  ) : selectedCompany._technologyValidationComplete && selectedCompany._validatedTechnologies && selectedCompany._validatedTechnologies.length > 0 ? (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      <Typography>
                        {selectedCompany._validatedTechnologies.length} {selectedCompany._validatedTechnologies.length === 1 ? 'technology' : 'technologies'} validated successfully.
                      </Typography>
                    </Alert>
                  ) : null}

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}

                  <CompanyForm
                    company={selectedCompany._savedCompany || {
                      ...selectedCompany,
                      id: selectedCompany.id || generateIdFromName(selectedCompany.name || ''),
                      name: selectedCompany.name || '',
                      technologies: selectedCompany._validatedTechnologies || []
                    }}
                    onSave={handleSaveCompany}
                    onCancel={handleCancelCompany}
                    isCreate={!selectedCompany._savedCompany}
                  />
                </>
              ) : (
                <Typography color="text.secondary">
                  Select a company from the list to review and edit.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BulkCompanyImport;
